import {createContext, useContext, useMemo, ReactNode} from 'react';
import {FEATURE_FLAG_KEYS} from '../../../shared/feature-flags';
import '../globals';

type FeatureFlagKey =
  (typeof FEATURE_FLAG_KEYS)[keyof typeof FEATURE_FLAG_KEYS];

type FeatureFlags = {
  [key in FeatureFlagKey]: boolean;
};

// Partial type for runtime since not all flags may be set initially
export type PartialFeatureFlags = Partial<FeatureFlags>;

const FeatureFlagsContext = createContext<PartialFeatureFlags>({});

interface FeatureFlagsProviderProps {
  children: ReactNode;
}

export function FeatureFlagsProvider({children}: FeatureFlagsProviderProps) {
  const featureFlags = useMemo(() => window.featureFlags ?? {}, []);

  return (
    <FeatureFlagsContext.Provider value={featureFlags}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

/**
 * Hook to access all feature flags
 * @returns Object containing all feature flags
 */
export function useFeatureFlags(): PartialFeatureFlags {
  return useContext(FeatureFlagsContext);
}

/**
 * Hook to check if a specific feature flag is enabled
 * @param flagKey - The feature flag key to check
 * @param defaultValue - Default value if flag is not found (default: false)
 * @returns Whether the feature flag is enabled
 */
export function useFeatureFlag(
  flagKey: FeatureFlagKey,
  defaultValue: boolean = false
): boolean {
  const flags = useContext(FeatureFlagsContext);
  return flags[flagKey] ?? defaultValue;
}

/**
 * Component that conditionally renders children based on a feature flag
 */
interface FeatureFlagProps {
  flagKey: FeatureFlagKey;
  children: ReactNode;
  fallback?: ReactNode;
}

export function FeatureFlag({
  flagKey,
  children,
  fallback = null,
}: FeatureFlagProps) {
  const isEnabled = useFeatureFlag(flagKey);

  if (!isEnabled) {
    return fallback;
  }

  return children;
}

FeatureFlag.displayName = 'FeatureFlag';
